/**
 * Gate de privacidad de módulos sensibles — lado frontend del backoffice.
 *
 * El backend excluye el contenido conversacional de módulos sensibles
 * (hoy `coach`, claim de privacidad de Orborix Coach): transcript/reveal/
 * span-io/prompt-log responden **403 `gated_module`**, `/full` nulifica el
 * diálogo con `gated: true`, y los memory facts salen con `body: null` +
 * `gated: true`. Este helper espeja ese contrato para que la UI muestre un
 * placeholder sobrio en vez de errores crudos o botones que van a fallar.
 *
 * **Espejo, no fuente**: la fuente única de "módulo sensible" es
 * `SENSITIVE_MODULE_PRIVATE` en `packages/memory-layer/src/types.ts`
 * (compartida con el cifrado at-rest y el gate del backend en
 * `apps/backend/src/routes/admin/_privacy.ts`). El admin no importa ese
 * package (arrastraría deps de backend al bundle), así que la lista se
 * espeja acá a mano — si se agrega un módulo sensible, actualizar ambos.
 */
import { AdminApiError } from '../api/admin.js';

const GATED_MODULES: readonly string[] = ['coach'];

/**
 * Kill-switch TEMPORAL del gate (espejo del `ADMIN_SENSITIVE_GATE` del
 * backend — fase de tuning pre-lanzamiento, decisión del dueño 2026-07-07):
 * con `VITE_ADMIN_SENSITIVE_GATE=off` la UI no oculta Chat/Replay ni pinta
 * placeholders preventivos. Si el backend siguiera gateando, los manejos de
 * 403 `gated_module` (isGatedModuleError) cubren igual. Al lanzar: sacar
 * ambos env. SOLO el literal `off` desactiva; default = estricto.
 */
const GATE_DISABLED = import.meta.env['VITE_ADMIN_SENSITIVE_GATE'] === 'off';

export const GATED_MODULE_ERROR_CODE = 'gated_module';

/** Copy única del placeholder de contenido gateado. */
export const GATED_CONTENT_PLACEHOLDER =
  'Contenido protegido — módulo sensible (claim de privacidad de Coach)';

/**
 * ¿El módulo de la sesión / el originModule del fact es sensible?
 * Match exacto (`coach`) o por prefijo de submódulo (`coach/ansiedad`) —
 * misma semántica que el backend.
 */
export function isGatedModule(moduleId: string | null | undefined): boolean {
  if (GATE_DISABLED) return false;
  if (!moduleId) return false;
  return GATED_MODULES.some(
    (sensitive) => moduleId === sensitive || moduleId.startsWith(`${sensitive}/`),
  );
}

/** ¿El error es el 403 `gated_module` del backend? */
export function isGatedModuleError(err: unknown): boolean {
  return err instanceof AdminApiError && err.code === GATED_MODULE_ERROR_CODE;
}
